import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Panorama } from '@/modules/defines/interface';
interface PanoramaState {
  movingPosition: [number, number];
  id: number;
  spaceId: number;
  panorama: Panorama[];
  selectedPanoramaId: number;
  expand: boolean;
  currentPosition: [number, number, number];
  hotspotButtonData: Record<number, Record<string, number>>;
  indexFloor: number[];
  subFloor: number[][];
  isFloorOpen: boolean[];
  pageIndex: number;
  minimapPositionOffset: { dx: number; dy: number };
  initialPositionOffset: { dx: number; dy: number };
  arrowLong: number;
  moveByWidget: boolean;
  setMovingPosition: (position: [number, number]) => void;
  setId: (id: number) => void;
  setSpaceId: (spaceId: number) => void;
  setPanorama: (panorama: Panorama[]) => void;
  setSelectedPanoramaId: (id: number) => void;
  setExpand: (expand: boolean) => void;
  toggleExpand: () => void;
  setIndexFloor: (indexFloor: number[]) => void;
  setSubFloor: (subFloor: number[][]) => void;
  resetIsFloorOpen: (length: number) => void;
  setIsFloorOpen: (floorIndex: number) => void;
  setPageIndex: (pageIndex: number) => void;
  setCurrentPosition: (position: [number, number, number]) => void;
  setHotspotButtonData: (data: Record<number, Record<string, number>>) => void;
  setMinimapPositionOffset: (offset: { dx: number; dy: number }) => void;
  setInitialPositionOffset: (offset: { dx: number; dy: number }) => void;
  updateMinimapPositionOffset: (
    deltaX: number,
    deltaY: number,
    contentSize: { width: number; height: number }
  ) => void;
  setArrowLong: (arrowLong: number) => void;
  setMoveByWidget: (moveByWidget: boolean) => void;
}

// 초기값을 로컬 스토리지에서 가져오는 함수
// const getLocalStorageValue = (key: string, defaultValue: any) => {
//   if (typeof window !== 'undefined') {
//     const savedValue = localStorage.getItem(key);
//     return savedValue ? JSON.parse(savedValue) : defaultValue;
//   }
//   return defaultValue;
// };

// Zustand store 생성
const usePanoramaStore = create<PanoramaState>()(
  subscribeWithSelector((set, _get) => ({
    movingPosition: [0, 0],
    id: 0,
    spaceId: 0,
    panorama: [],
    selectedPanoramaId: 0,
    expand: false,
    currentPosition: [0, 0, 0],
    hotspotButtonData: {
      0: { id: 0, distance: 100 },
      1: { id: 0, distance: 100 },
      2: { id: 0, distance: 100 },
      3: { id: 0, distance: 100 },
      4: { id: 0, distance: 100 },
      5: { id: 0, distance: 100 },
      6: { id: 0, distance: 100 },
      7: { id: 0, distance: 100 },
      8: { id: 0, distance: 100 },
      9: { id: 0, distance: 100 },
      10: { id: 0, distance: 100 },
      11: { id: 0, distance: 100 },
      12: { id: 0, distance: 100 },
      13: { id: 0, distance: 100 },
      14: { id: 0, distance: 100 },
      15: { id: 0, distance: 100 },
      16: { id: 0, distance: 100 },
    },
    indexFloor: [],
    subFloor: [],
    isFloorOpen: [],
    minimapPositionOffset: { dx: 0, dy: 0 },
    initialPositionOffset: { dx: 0, dy: 0 },
    arrowLong: 0,
    moveByWidget: false,
    pageIndex: -1,
    setMovingPosition: (position) => {
      set({ movingPosition: position });
    },
    setId: (id: number) => {
      set({ id });
    },
    setSelectedPanoramaId: (selectedPanoramaId) => {
      set({ selectedPanoramaId });
    },
    setCurrentPosition: (position) => {
      set({ currentPosition: position });
    },
    setHotspotButtonData: (data) => set({ hotspotButtonData: data }),
    setArrowLong: (arrowLong) => {
      set({ arrowLong });
    },
    setSpaceId: (spaceId: number) => {
      set({ spaceId });
    },
    setPanorama: (panorama) => set({ panorama }),
    setIndexFloor: (indexFloor) => set({ indexFloor }),
    setSubFloor: (subFloor) => set({ subFloor }),
    resetIsFloorOpen: (length: number) => {
      set({ isFloorOpen: new Array(length).fill(false) });
    },
    setIsFloorOpen: (floorIndex: number) => {
      set((state) => {
        const newIsFloorOpen = state.isFloorOpen.map((open, idx) =>
          idx === floorIndex ? true : open
        );
        return { isFloorOpen: newIsFloorOpen };
      });
    },
    setPageIndex: (pageIndex) => set({ pageIndex }),
    setExpand: (expand) => set({ expand }),
    toggleExpand: () => set((state) => ({ expand: !state.expand })),
    setMinimapPositionOffset: (offset) =>
      set({ minimapPositionOffset: offset }),
    setInitialPositionOffset: (offset) =>
      set({ initialPositionOffset: offset }),
    updateMinimapPositionOffset: (deltaX, deltaY, contentSize) => {
      set((state) => {
        const newDx = state.minimapPositionOffset.dx + deltaX;
        const newDy = state.minimapPositionOffset.dy + deltaY;
        const { dx: initialDx, dy: initialDy } = state.initialPositionOffset;
        const boundedDx = Math.min(
          Math.max(newDx, -contentSize.width / 2 + initialDx),
          contentSize.width / 2 + initialDx
        );
        const boundedDy = Math.min(
          Math.max(newDy, -contentSize.height / 2 + initialDy),
          contentSize.height / 2 + initialDy
        );
        return { minimapPositionOffset: { dx: boundedDx, dy: boundedDy } };
      });
    },
    setMoveByWidget: (moveByWidget) => set({ moveByWidget }),
  }))
);

export default usePanoramaStore;
